// React and MobX
import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Box, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

// Core
import { useRouter } from "Core/Utils";
import { Loader } from "Core/Components/Loader";

// Custom Components
import { Error } from "Custom/Components/Error";
import { InspectionStagePresetHelper } from "Custom/Content/Components/InspectionStageSVG";
import { ReactComponent as Camera } from "Custom/Content/camera.svg";
import { ReactComponent as Document } from "Custom/Content/documentLarge.svg";

// Custom Styles
import { DashboardPageStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { InspectionPageStyles } from "Custom/StylesAppSpecific/DashboardSubViews/InspectionPageStyling";

// Custom Models and ViewModels
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { InspectionViewModel } from "Custom/ViewModels/DashboardSubViewModels/InspectionViewModel";
import { InspectionDocumentViewModel } from "Custom/ViewModels/DashboardSubViewModels/InspectionDocumentViewModel";
import { PrivateBlob } from "Custom/Components/Image/PrivateImage";
import { HoverScrollableGallery } from "Core/Components/HoverScrollableGallery";
import { InspectionDocumentModel } from "Custom/Models/DashboardModels/InspectionDocumentModel";
import DocumentListViewer from "Core/Components/DocumentListViewer";

export const Inspection: React.FC = () => {
    // #region Code Behind

    const router = useRouter();
    const theme = useTheme();

    //console.log("BUILDVIEW: PropertyId", router.match.params);
    const propertyId = (router.match.params as any).propertyId;

    const dashboardPageStyles = DashboardPageStyles();
    const inspectionPageStyles = InspectionPageStyles();

    const [viewModel] = useState(() => new InspectionViewModel(propertyId));
    const [inspectionDocumentViewModel] = useState(() => new InspectionDocumentViewModel(propertyId));

    const [viewerMode, setViewerMode] = useState<"image" | "document" | null>(null);
    const [clickedStageIndex, setClickedStageIndex] = useState<number | null>(null);

    const handleStageClick = (index: number, mode: "image" | "document") => {
        if (viewerMode === mode && clickedStageIndex === index) {
            // If clicking the same stage and mode, close it
            setViewerMode(null);
            setClickedStageIndex(null);
        } else {
            // If clicking a different stage or mode, open it
            setViewerMode(mode);
            setClickedStageIndex(index);
        }
    };

    const getTextStyle = (inspectionStageStatusType: any) => {
        if (inspectionStageStatusType != null) {
            return {
                color: "black",
            };
        }
    };
    const getSubTextStyle = (inspectionStageStatusType: any) => {
        let colour = "";
        switch (inspectionStageStatusType) {
            case 2:
                colour = "#dc3545"; // Red
                break;
            case 4:
                colour = "#5bb44e"; // Green
                break;
        }
        return {
            color: colour,
        };
    };

    // #endregion Code Behind

    const renderBusy = () => <Loader loadingText="Fetching build information..." />;
    const renderError = () => <Error errorText="Failed to retrieve the build information." />;
    //     isDesktop ? (
    //         <>
    //             <Box className={inspectionPageStyles.titleRow}>
    //                 <Typography className={inspectionPageStyles.title} variant={"h5"}>
    //                     Build
    //                 </Typography>
    //             </Box>
    //             <Box className={inspectionPageStyles.titleBorder} />
    //         </>
    //     ) : (
    //         <Box className={dashboardPageStyles.titleRow}>
    //             <Typography variant={"h5"}>Inspection</Typography>
    //         </Box>
    //     );

    const renderStages = () => (
        <Box className={inspectionPageStyles.stages}>
            <Box className={inspectionPageStyles.gradient} />
            {Array.from(viewModel.inspectionStages)
                .slice()
                .map((stage, index) => {
                    const inspectionImages = inspectionDocumentViewModel.documents.filter(
                        document =>
                            document.inspectionStageId === stage.inspectionStageId &&
                            document.canViewInCustomerPortal &&
                            (document.imageUrl ?? "").match(/\.(jpg|jpeg|png)(\?.*)?$/i),
                    );

                    const inspectionDocuments = inspectionDocumentViewModel.documents.filter(
                        document =>
                            document.inspectionStageId === stage.inspectionStageId &&
                            document.canViewInCustomerPortal &&
                            !(document.imageUrl ?? "").match(/\.(jpg|jpeg|png)(\?.*)?$/i),
                    );

                    return (
                        <div key={index} className={inspectionPageStyles.mainAccordion}>
                            <div key={index} className={viewerMode && clickedStageIndex === index ? inspectionPageStyles.stageContainer : ""}>
                                <div className={`${inspectionPageStyles.stage} ${viewerMode && clickedStageIndex === index ? inspectionPageStyles.activeStage : ""}`} key={index}>
                                    <Box>
                                        {/* Dummy box for link - do not remove */}
                                        <Box />
                                        <Box className={inspectionPageStyles.icon}>{InspectionStagePresetHelper(stage.inspectionStageColor, stage.inspectionStageStatusType)}</Box>
                                        {/* Dummy box for link - do not remove */}
                                        <Box />
                                    </Box>

                                    <Box
                                        style={{
                                            display: "flex",
                                            flex: 1,
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: "10px",
                                        }}
                                    >
                                        <Box className={inspectionPageStyles.stageTextRow}>
                                            <Typography className={inspectionPageStyles.stageTitle} style={getTextStyle(stage.inspectionStageStatusType)}>
                                                {stage.inspectionStageName}
                                            </Typography>
                                            <Typography className={inspectionPageStyles.stageSubTitle} style={getTextStyle(stage.inspectionStageStatusType)}>
                                                {stage.inspectionStageDateFormatted}
                                            </Typography>
                                        </Box>

                                        <Box style={{ display: "flex", gap: "12px" }}>
                                            {inspectionImages.length > 0 && (
                                                <Box className={inspectionPageStyles.stageIcon} style={{ cursor: "pointer" }} onClick={() => handleStageClick(index, "image")}>
                                                    <Camera height={18} width={27} />
                                                    {inspectionImages.length}
                                                </Box>
                                            )}

                                            {inspectionDocuments.length > 0 && (
                                                <Box className={inspectionPageStyles.stageIcon} style={{ cursor: "pointer" }} onClick={() => handleStageClick(index, "document")}>
                                                    <Document height={28} width={23} />
                                                    {inspectionDocuments.length}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </div>
                                <div>
                                    {/* Expanded image section */}
                                    {inspectionImages.length > 0 && viewerMode === "image" && clickedStageIndex === index && renderStageImages(inspectionImages)}
                                    {inspectionDocuments.length > 0 && viewerMode === "document" && clickedStageIndex === index && renderStageDocuments(inspectionDocuments)}
                                </div>
                            </div>
                        </div>
                    );
                })}
        </Box>
    );

    //stage images
    const renderStageImages = (inspectionImages: InspectionDocumentModel[]) => {
        return <>{inspectionImages && inspectionImages.length > 0 && <HoverScrollableGallery className={inspectionPageStyles.galleryContainer} images={inspectionImages} />}</>;
    };

    //stage documents
    const renderStageDocuments = (inspectionDocuments: InspectionDocumentModel[]) => {
        return (
            <>
                <DocumentListViewer documents={inspectionDocuments} title="Stage Document Viewer" className={inspectionPageStyles.stageDocumentBlock} />
            </>
        );
    };

    return useObserver(() => (
        <Box className={`${dashboardPageStyles.root} ${inspectionPageStyles.root}`}>
            <Box className={`${dashboardPageStyles.workspace} ${inspectionPageStyles.workspace}`}>
                {viewModel.ApiStatus === ApiStatus.success && <React.Fragment>{renderStages()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
            </Box>
        </Box>
    ));
};
